/**
 * ReviewComposer — the compact feedback row at the bottom of the review
 * page: free-text "What went wrong?" plus Bad/Good verdict pills, a
 * Dismiss action and a Save & next action. Saving upserts the label
 * (Bad/Good → `approved`, text → `notes`, `review_status='reviewed'`) and
 * advances the queue.
 *
 * Dismiss (TRA-908) is the same upsert with `review_status='dismissed'`,
 * for a flag that doesn't belong in the eval at all — it's not a verdict,
 * so it fires on one click rather than arming a pill, and it carries no
 * `approved` claim. Dismissed rows leave the sidebar unless its
 * `dismissed` filter is on; re-saving one as Bad/Good un-dismisses it.
 *
 * Conversation turns use appendComment: each save adds feedback in place,
 * and queue navigation/dismiss shortcuts stay disabled.
 * Mount with `key={item.trace_id}` so state resets per queue item.
 */

import { useEffect, useId, useState } from 'react'
import { Flag } from 'lucide-react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'

import { parseVerdictSummary } from '@/components/review/flagSource'
import { reviewComments } from '@/components/review/reviewComments'
import { feedbackSnapshot, nextVersion } from '@/components/review/reviewVersion'
import { verdictPillClass } from '@/components/review/verdictPill'
import { useUpsertLabel } from '@/hooks/useLabels'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import type { LabelUpsert, ReviewQueueItem } from '@/api/generated/model'

type Verdict = 'bad' | 'good'
type ReviewStatus = NonNullable<LabelUpsert['review_status']>

interface ReviewComposerProps {
  item: Pick<ReviewQueueItem, 'trace_id' | 'trace_key' | 'notes' | 'review_status' | 'approved'>
  appendComment?: boolean
  flagged?: boolean
  flagVerdict?: 'good' | 'bad' | null
  readOnly?: boolean
  expectedUpdatedAt: string | null
  // False while the trajectory is loading or failed to load — a review
  // saved without seeing the conversation would be feedback on nothing.
  canSave: boolean
  onSaved: () => void
}

export function ReviewComposer({
  item,
  canSave,
  onSaved,
  appendComment = false,
  expectedUpdatedAt,
  flagged = false,
  flagVerdict = null,
  readOnly = false,
}: ReviewComposerProps): React.JSX.Element | null {
  const notesId = useId()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  // Reviewed rows re-open with their saved feedback; fresh flags start
  // from the flag reason so the reviewer edits rather than retypes.
  //
  // The dream scan's `[good]`/`[bad]` marker is OUR encoding of its
  // structured lean, not part of the reason — it belongs in the verdict
  // pill, which is the control for exactly that (TRA-914). So the text
  // box gets the reason alone, and the marker seeds the pill instead of
  // being pasted into prose the reviewer is about to sign their name to.
  const flag = parseVerdictSummary(item.notes)
  const [notes, setNotes] = useState(appendComment ? '' : flag.text)
  const [version, setVersion] = useState(expectedUpdatedAt)
  // The feedback the composer was seeded from. Tags save on their own
  // and bump the row's `updated_at`; when the row comes back with a newer
  // version but the same feedback, the save must carry that newer version
  // or the api refuses it with a 409 until the page is refreshed. A row
  // whose feedback DID change keeps the old version so the 409 still
  // guards it (see reviewVersion.ts).
  const [seed, setSeed] = useState(() => feedbackSnapshot(item))
  useEffect(() => {
    setVersion((current) =>
      nextVersion({ current, rowVersion: expectedUpdatedAt, seed, row: feedbackSnapshot(item) }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the three feedback fields are the deps; `item` is a new object every render
  }, [expectedUpdatedAt, item.notes, item.approved, item.review_status, seed])
  // An UNREVIEWED row's `approved` is always false — it means "nobody has
  // signed off yet", not "this run was bad" — so the judge's lean is the
  // honest starting pill there. Once reviewed, the saved verdict is.
  const [verdict, setVerdict] = useState<Verdict>(
    item.review_status === 'unreviewed'
      ? (flag.lean ?? 'bad')
      : item.approved
        ? 'good'
        : 'bad',
  )
  const upsert = useUpsertLabel()

  async function save(reviewStatus: ReviewStatus): Promise<void> {
    if (!canSave || upsert.isPending) return
    try {
      // rubric_scores is deliberately omitted — the upsert preserves any
      // stored scores when the field is absent.
      const saved = await upsert.mutateAsync({
        data: {
          trace_id: item.trace_id,
          trace_key: item.trace_key,
          // A dismissal makes no claim about the run, so it can't leave a
          // stale `approved: true` behind for the eval-PR agent to read.
          approved: reviewStatus === 'dismissed' ? false : verdict === 'good',
          notes,
          review_status: reviewStatus,
          append_notes: appendComment,
          expected_updated_at: version,
        },
      })
      if (saved.status !== 200) throw new Error('Review save returned no label')
      setVersion(saved.data.updated_at)
      setSeed(feedbackSnapshot(saved.data))
      if (appendComment) setNotes((current) => current === notes ? '' : current)
      setOpen(false)
      setEditing(false)
      onSaved()
    } catch (error) {
      const verb = reviewStatus === 'dismissed' ? 'dismiss' : 'save'
      toast.error(`Failed to ${verb} review: ${String(error)}`)
    }
  }

  async function handleSave(): Promise<void> {
    await save('reviewed')
  }

  async function handleDismiss(): Promise<void> {
    await save('dismissed')
  }

  // `d` dismisses — a browser-level key subscription, like the page's j/k.
  // It lives here rather than in ReviewPage because the mutation does (⌘↵
  // is already composer-owned), and it stands down inside text fields so
  // typing "d" in the notes stays typing.
  useEffect(() => {
    if (appendComment) return
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key !== 'd' || e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target as HTMLElement
      if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') return
      if (!canSave || upsert.isPending) return
      e.preventDefault()
      void handleDismiss()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleDismiss is redefined every render; the guarded inputs are the real deps
  }, [appendComment, canSave, upsert.isPending, notes, version, item.trace_id, item.trace_key])

  const editor = (
    <div className={appendComment ? '' : 'border-t border-border bg-background'}>
      <div className={appendComment ? '' : 'mx-auto w-full max-w-[660px] px-8 py-3.5'}>
        <div className="flex flex-col gap-3 rounded-md border border-border/70 px-3 pb-3 pt-3 focus-within:ring-1 focus-within:ring-ring/20 focus-within:border-muted-foreground">
          <label htmlFor={notesId} className="sr-only">
            {appendComment ? 'Add a comment' : 'What went wrong?'}
          </label>
          {/* The box grows with the feedback: `field-sizing: content` sizes the
              textarea to its text, floored at two lines and capped at ten
              before it scrolls internally. `rows` is ignored where
              `field-sizing` is supported and is the two-line fallback where it
              isn't. */}
          <textarea
            id={notesId}
            autoFocus={appendComment}
            rows={2}
            placeholder={appendComment ? 'Add a comment' : 'What went wrong?'}
            disabled={upsert.isPending}
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value)
            }}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault()
                void handleSave()
              }
            }}
            className="max-h-[10lh] min-h-[2lh] w-full resize-none overflow-y-auto bg-transparent text-sm leading-relaxed text-foreground outline-none [field-sizing:content] placeholder:text-muted-foreground/60"
          />
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              aria-pressed={verdict === 'bad'}
              disabled={upsert.isPending}
              onClick={() => {
                setVerdict('bad')
              }}
              className={verdictPillClass(verdict === 'bad', 'bad')}
            >
              Bad
            </button>
            <button
              type="button"
              aria-pressed={verdict === 'good'}
              disabled={upsert.isPending}
              onClick={() => {
                setVerdict('good')
              }}
              className={verdictPillClass(verdict === 'good', 'good')}
            >
              Good
            </button>
            {/* Hairline, not a verdict pill — dismissing says "this flag
                doesn't belong here", which is a different axis from
                bad/good and must not look like a third choice on it. */}
            {!appendComment && (
              <button
                type="button"
                disabled={upsert.isPending || !canSave}
                onClick={() => {
                  void handleDismiss()
                }}
                className="h-8 rounded-md border border-border/70 bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring text-muted-foreground hover:bg-muted disabled:opacity-50"
              >
                Dismiss
              </button>
            )}
            <button
              type="button"
              disabled={upsert.isPending || !canSave}
              onClick={() => {
                void handleSave()
              }}
              className="ml-auto h-8 rounded-md bg-primary/85 px-3.5 text-sm hover:bg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 text-primary-foreground disabled:opacity-50"
            >
              {upsert.isPending ? 'Saving…' : appendComment ? 'Save review' : 'Save & next'}
            </button>
          </div>
        </div>
        <p className="mt-1.5 text-right text-xs text-muted-foreground/70">
          {appendComment ? '⌘↵ save' : 'j/k queue · ⌘↵ save · d dismiss'}
        </p>
      </div>
    </div>
  )

  if (!appendComment) return editor
  if (readOnly && !flagged) return null
  const comments = reviewComments(item.notes)
  const latest = flagged ? comments[comments.length - 1] : undefined
  const showEditor = !readOnly && (!flagged || editing || notes !== '')

  return (
    <Popover open={open} onOpenChange={(next) => {
      setOpen(next)
      if (!next && notes === '') setEditing(false)
    }}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label={flagged ? 'View feedback' : 'Flag turn'}
          title={flagged ? 'View feedback' : 'Flag turn'}
          className={`h-7 w-6 rounded-sm p-0 ${flagVerdict === 'good' ? 'text-success/80 hover:text-success/80' : flagVerdict === 'bad' ? 'text-destructive/80 hover:text-destructive/80' : 'text-muted-foreground'} ${
            flagged || open || notes !== ''
              ? 'opacity-100'
              : 'opacity-0 group-hover/trace-row:opacity-100 group-focus-within/trace-row:opacity-100 focus-visible:opacity-100'
          }`}
        >
          <Flag className={flagged ? 'h-3.5 w-3.5 fill-current' : 'h-3.5 w-3.5'} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="hidden w-[360px] max-w-[calc(100vw-24px)] rounded-sm p-3 shadow-none lg:block"
        aria-label="Review this turn"
      >
        {flagged && (
          <div className="mb-3 max-h-60 space-y-1.5 overflow-y-auto px-1 py-1">
            <div className="flex items-baseline justify-between gap-3">
              {flagVerdict !== null && (
                <span className={`font-mono text-[10px] uppercase tracking-wider ${flagVerdict === 'good' ? 'text-success' : 'text-destructive/80'}`}>
                  {flagVerdict}
                </span>
              )}
              {latest !== undefined && latest.createdAt !== null && (
                <time dateTime={latest.createdAt} title={new Date(latest.createdAt).toLocaleString()} className="ml-auto text-[11px] tabular-nums text-muted-foreground">
                  {new Date(latest.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </time>
              )}
            </div>
            {latest !== undefined && latest.text !== '' && <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{latest.text}</p>}
          </div>
        )}
        {showEditor ? editor : (
          <div className="flex items-center justify-between gap-3">
            {!readOnly && <Button variant="outline" size="sm" onClick={() => { setEditing(true) }}>Add comment</Button>}
            {comments.length > 1 ? <Link className="text-xs text-muted-foreground underline-offset-4 hover:underline" to={`/review?item=${encodeURIComponent(item.trace_id)}`}>View all feedback</Link> : null}
            {readOnly && latest === undefined && <p className="text-sm text-muted-foreground">No comment recorded.</p>}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
